// TODO: Most of these functions should have unit tests
import React from 'react';
import { MapRef } from 'react-map-gl/mapbox';
import { LatLong, LotSideIndexes, Position } from '../types/position';
import { RegridResponse } from '../../../models/regrid.model.tsx';

export const spaceBetweenTwoPoints = (
  pointA: [number, number],
  pointB: [number, number]
) => {
  return [(pointA[0] + pointB[0]) / 2, (pointA[1] + pointB[1]) / 2];
};

export const getTwoMiddlePoints = (coordinates: [number, number][]) => {
  const middleIndex = Math.floor(coordinates.length / 2);
  return [coordinates[middleIndex - 1], coordinates[middleIndex]];
};

export function degToRad(deg: number) {
  return deg * (Math.PI / 180);
}

export function radToDeg(rad: number) {
  return rad * (180 / Math.PI);
}

export const findCenterLatLon = (middlePoints: LatLong[]) => {
  const points: { longitude: number; latitude: number }[] | [] =
    middlePoints?.slice(0, 4) as { longitude: number; latitude: number }[];
  let x = 0;
  let y = 0;
  let z = 0;

  points.forEach((point) => {
    const lat = degToRad(point?.latitude || 0);
    const lon = degToRad(point?.longitude || 0);

    x += Math.cos(lat) * Math.cos(lon);
    y += Math.cos(lat) * Math.sin(lon);
    z += Math.sin(lat);
  });

  const totalPoints = points.length;
  x /= totalPoints;
  y /= totalPoints;
  z /= totalPoints;

  const centralLon = radToDeg(Math.atan2(y, x));
  const centralLat = radToDeg(Math.atan2(z, Math.sqrt(x * x + y * y)));

  return { latitude: centralLat, longitude: centralLon };
};

export const isPointInPolygon = (point: Position, polygon: Position[]) => {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

// Finds the 'center of gravity' of a polygon.
export const getCentroid = (coordinates: Position[]) => {
  let x = 0,
    y = 0,
    area = 0;
  const n = coordinates.length;

  for (let i = 0; i < n; i++) {
    const [x1, y1] = coordinates[i];
    const [x2, y2] = coordinates[(i + 1) % n];
    const a = x1 * y2 - x2 * y1;
    x += (x1 + x2) * a;
    y += (y1 + y2) * a;
    area += a;
  }

  area *= 0.5;
  x /= 6 * area;
  y /= 6 * area;

  let centroid: [number, number] = [x, y];

  // Check if the centroid is inside the polygon
  if (!isPointInPolygon(centroid, coordinates)) {
    // If not, adjust the centroid to the first vertex (as a simple fallback)
    centroid = coordinates[0];
  }

  return centroid;
};

export const calculatePixelSizeFromMeters = (
  meters: number,
  mapRef: React.RefObject<MapRef>,
  addressDetails: LatLong | undefined
) => {
  if (mapRef.current) {
    const centerPixel = mapRef.current.project([
      addressDetails?.longitude || 0,
      addressDetails?.latitude || 0,
    ]);

    const northCoordinates: [number, number] = [
      addressDetails?.longitude || 0,
      (addressDetails?.latitude || 0) + meters / 111320,
    ]; // Roughly 1 meter in latitude
    const northPixel = mapRef.current.project(northCoordinates);

    return Math.abs(northPixel.y - centerPixel.y);
  }
};

export const expandPolygonCoordinates = (
  coordinates: Position[],
  meters: number
) => {
  if (!coordinates || coordinates.length < 3) {
    console.error('Invalid coordinates array:', coordinates);
    return coordinates;
  }

  const EARTH_RADIUS = 6378137; // in meters

  if (
    !coordinates[0] ||
    coordinates[0].length !== 2 ||
    isNaN(coordinates[0][0]) ||
    isNaN(coordinates[0][1])
  ) {
    console.error('Invalid first coordinate:', coordinates[0]);
    return coordinates;
  }

  // Degrees aren't always a consistent amount of meters, so we need to convert them to meters at the latitude of the first coordinate
  const metersToDegreesLat = meters / (EARTH_RADIUS * (Math.PI / 180));
  const metersToDegreesLon =
    meters /
    (EARTH_RADIUS *
      (Math.PI / 180) *
      Math.cos(coordinates[0][1] * (Math.PI / 180)));

  // Calculate normals for each vertex
  const expandedCoords = coordinates.map((coord, i) => {
    if (!coord || coord.length !== 2 || isNaN(coord[0]) || isNaN(coord[1])) {
      console.error('Invalid coordinate at index', i, ':', coord);
      return coord;
    }

    const prev = coordinates[(i - 1 + coordinates.length) % coordinates.length];
    const next = coordinates[(i + 1) % coordinates.length];

    if (!prev || !next || prev.length !== 2 || next.length !== 2) {
      console.error('Invalid adjacent coordinates at index', i);
      return coord;
    }

    // Calculate normalized vectors for adjacent edges
    const v1 = {
      x: coord[0] - prev[0],
      y: coord[1] - prev[1],
    };
    const v2 = {
      x: next[0] - coord[0],
      y: next[1] - coord[1],
    };

    // Calculate normals
    const n1 = {
      x: -v1.y,
      y: v1.x,
    };
    const n2 = {
      x: -v2.y,
      y: v2.x,
    };

    // Calculate lengths
    const len1 = Math.sqrt(n1.x * n1.x + n1.y * n1.y);
    const len2 = Math.sqrt(n2.x * n2.x + n2.y * n2.y);

    // Handle zero-length vectors
    if (len1 === 0 || len2 === 0) {
      console.warn('Zero-length normal vector at index', i);
      return coord;
    }

    // Normalize and average the normals
    const avgNormal = {
      x: (n1.x / len1 + n2.x / len2) / 2,
      y: (n1.y / len1 + n2.y / len2) / 2,
    };

    const lenAvg = Math.sqrt(
      avgNormal.x * avgNormal.x + avgNormal.y * avgNormal.y
    );

    if (lenAvg === 0) {
      console.warn('Zero-length average normal at index', i);
      return coord;
    }

    return [
      coord[0] + (avgNormal.x / lenAvg) * metersToDegreesLon,
      coord[1] + (avgNormal.y / lenAvg) * metersToDegreesLat,
    ];
  });

  return expandedCoords;
};

export const getMiddleBetweenCoordinates = (
  point1: [number, number] | undefined,
  point2: [number, number] | undefined
) => {
  if (point1 && point2) {
    return {
      longitude: (point1?.[0] + point2?.[0]) / 2,
      latitude: (point1?.[1] + point2?.[1]) / 2,
    };
  }
};

export const calcAngle = (px = 0, py = 0, ax = 0, ay = 0) => {
  // point slope formula
  return (Math.atan((px - ax) / (py - ay)) * 180) / Math.PI;
};

export const getAngleBetweenThreePoints = (
  pointA: [number, number],
  pointB: [number, number],
  pointC: [number, number]
) => {
  // Description of formula: https://www.youtube.com/watch?v=fVXyTQ06csY
  // BAV = (Ax - Bx, Ay - By)
  const BAVector = [pointA[0] - pointB[0], pointA[1] - pointB[1]]; // u
  // BCV = (Cx - Bx, Cy - By)
  const BCVector = [pointC[0] - pointB[0], pointC[1] - pointB[1]]; // v

  const cosOfAngleNumerator =
    BAVector[0] * BCVector[0] + BAVector[1] * BCVector[1];
  const cosOfAngleDenominator =
    Math.sqrt(Math.pow(BAVector[0], 2) + Math.pow(BAVector[1], 2)) *
    Math.sqrt(Math.pow(BCVector[0], 2) + Math.pow(BCVector[1], 2));
  const cosOfAngle = cosOfAngleNumerator / cosOfAngleDenominator;

  const radsOfAngle = Math.acos(cosOfAngle);

  const answer = radsOfAngle * (180 / Math.PI);
  return answer;
};

const findPointAtDistance = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  d: number
): [number, number] => {
  // Calculate the distance between the two points
  const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

  // Calculate the new point at distance d from (x1, y1) towards (x2, y2)
  const newX = x1 + (d * (x2 - x1)) / dist;
  const newY = y1 + (d * (y2 - y1)) / dist;

  return [newX, newY];
};

export const getSetbackCoordinates = (
  coordinates: [number, number][],
  lotSideIndexes: LotSideIndexes,
  regridData?: RegridResponse
) => {
  const newCoordinates = [...coordinates];
  const zoningInfo = regridData?.zoningInformation;

  const degreeToFeetHorizontal = 1 / 288200;

  // side 3

  // if current sides index is the front side index use minimumFrontSetbackFeet if it's more than 0
  // if current sides index is the back side index use minimumRearSetbackFeet if it's more than 0
  // otherwise use minimumSideSetbackFeet if it's greater than 0
  // if any values are less than 0, just use 0
  const side3FeetRemoved =
    lotSideIndexes.frontSideIndex === 3 && zoningInfo?.minimumFrontSetbackFeet
      ? zoningInfo?.minimumFrontSetbackFeet > 0
        ? zoningInfo?.minimumFrontSetbackFeet
        : 0
      : lotSideIndexes.backSideIndex === 3 && zoningInfo?.minimumRearSetbackFeet
        ? zoningInfo?.minimumRearSetbackFeet > 0
          ? zoningInfo?.minimumRearSetbackFeet
          : 0
        : zoningInfo?.minimumSideSetbackFeet &&
            zoningInfo?.minimumSideSetbackFeet > 0
          ? zoningInfo?.minimumSideSetbackFeet
          : 0;
  const side3DegreesRemoved = side3FeetRemoved * degreeToFeetHorizontal;
  newCoordinates[0] = findPointAtDistance(
    coordinates[0][0],
    coordinates[0][1],
    coordinates[1][0],
    coordinates[1][1],
    side3DegreesRemoved
  );
  newCoordinates[4] = newCoordinates[0];
  newCoordinates[3] = findPointAtDistance(
    coordinates[3][0],
    coordinates[3][1],
    coordinates[2][0],
    coordinates[2][1],
    side3DegreesRemoved
  );

  // side 0
  const side0FeetRemoved =
    lotSideIndexes.frontSideIndex === 0 && zoningInfo?.minimumFrontSetbackFeet
      ? zoningInfo?.minimumFrontSetbackFeet > 0
        ? zoningInfo?.minimumFrontSetbackFeet
        : 0
      : lotSideIndexes.backSideIndex === 0 && zoningInfo?.minimumRearSetbackFeet
        ? zoningInfo?.minimumRearSetbackFeet > 0
          ? zoningInfo?.minimumRearSetbackFeet
          : 0
        : zoningInfo?.minimumSideSetbackFeet &&
            zoningInfo?.minimumSideSetbackFeet > 0
          ? zoningInfo?.minimumSideSetbackFeet
          : 0;
  const side0DegreesRemoved = side0FeetRemoved * degreeToFeetHorizontal;
  newCoordinates[0] = findPointAtDistance(
    newCoordinates[0][0],
    newCoordinates[0][1],
    newCoordinates[3][0],
    newCoordinates[3][1],
    side0DegreesRemoved
  );
  newCoordinates[4] = newCoordinates[0];
  newCoordinates[1] = findPointAtDistance(
    newCoordinates[1][0],
    newCoordinates[1][1],
    newCoordinates[2][0],
    newCoordinates[2][1],
    side0DegreesRemoved
  );

  // // side 1
  const side1FeetRemoved =
    lotSideIndexes.frontSideIndex === 1 && zoningInfo?.minimumFrontSetbackFeet
      ? zoningInfo?.minimumFrontSetbackFeet > 0
        ? zoningInfo?.minimumFrontSetbackFeet
        : 0
      : lotSideIndexes.backSideIndex === 1 && zoningInfo?.minimumRearSetbackFeet
        ? zoningInfo?.minimumRearSetbackFeet > 0
          ? zoningInfo?.minimumRearSetbackFeet
          : 0
        : zoningInfo?.minimumSideSetbackFeet &&
            zoningInfo?.minimumSideSetbackFeet > 0
          ? zoningInfo?.minimumSideSetbackFeet
          : 0;
  const side1DegreesRemoved = side1FeetRemoved * degreeToFeetHorizontal;
  newCoordinates[1] = findPointAtDistance(
    newCoordinates[1][0],
    newCoordinates[1][1],
    newCoordinates[0][0],
    newCoordinates[0][1],
    side1DegreesRemoved
  );
  newCoordinates[2] = findPointAtDistance(
    newCoordinates[2][0],
    newCoordinates[2][1],
    newCoordinates[3][0],
    newCoordinates[3][1],
    side1DegreesRemoved
  );

  // //side 2
  const side2FeetRemoved =
    lotSideIndexes.frontSideIndex === 2 && zoningInfo?.minimumFrontSetbackFeet
      ? zoningInfo?.minimumFrontSetbackFeet > 0
        ? zoningInfo?.minimumFrontSetbackFeet
        : 0
      : lotSideIndexes.backSideIndex === 2 && zoningInfo?.minimumRearSetbackFeet
        ? zoningInfo?.minimumRearSetbackFeet > 0
          ? zoningInfo?.minimumRearSetbackFeet
          : 0
        : zoningInfo?.minimumSideSetbackFeet &&
            zoningInfo?.minimumSideSetbackFeet > 0
          ? zoningInfo?.minimumSideSetbackFeet
          : 0;

  const side2DegreesRemoved = side2FeetRemoved * degreeToFeetHorizontal;
  newCoordinates[2] = findPointAtDistance(
    newCoordinates[2][0],
    newCoordinates[2][1],
    newCoordinates[1][0],
    newCoordinates[1][1],
    side2DegreesRemoved
  );
  newCoordinates[3] = findPointAtDistance(
    newCoordinates[3][0],
    newCoordinates[3][1],
    newCoordinates[0][0],
    newCoordinates[0][1],
    side2DegreesRemoved
  );
  return newCoordinates;
};

export const getHomeModelCorners = (
  selectedHomeObjectWidth: number | null | undefined,
  selectedHomeObjectLength: number | null | undefined,
  homePlacementLatitude: number,
  homePlacementLongitude: number,
  homePlacementRotation: number
) => {
  if (!selectedHomeObjectWidth || !selectedHomeObjectLength) {
    return [
      [0, 0],
      [0, 0],
      [0, 0],
      [0, 0],
      [0, 0],
    ] as Position[];
  }

  const homeModelWidthInMeters = (selectedHomeObjectWidth || 0) / 3.28;
  const homeModelHeightInMeters = (selectedHomeObjectLength || 0) / 3.28;

  const earth = 6378.137; //radius of the earth in kilometer
  const pi = Math.PI;

  const longM = 1 / (((2 * pi) / 360) * earth) / 1000;

  // start with pythagorean theorem to get distance of line between center point
  // and corner of home model
  const cSquared =
    (homeModelWidthInMeters / 2) * (homeModelWidthInMeters / 2) +
    (homeModelHeightInMeters / 2) * (homeModelHeightInMeters / 2);

  // length of line in meters
  const cLength = Math.sqrt(cSquared);

  // length of line in degrees
  const cLengthDegrees = cLength * longM + 0.000005;

  const calculateAngleToCorner = (
    cSideLength: number,
    aSideLength: number,
    bHypotenuseLength: number
  ) => {
    // Law of Cosines to get angle from center to corner
    const angleInRadians = Math.acos(
      (bHypotenuseLength * bHypotenuseLength +
        cSideLength * cSideLength -
        aSideLength * aSideLength) /
        (2 * bHypotenuseLength * cSideLength)
    );

    return angleInRadians * (180 / Math.PI);
  };

  // angle from center of home model to corner
  const angleToCorner = calculateAngleToCorner(
    homeModelWidthInMeters / 2,
    homeModelHeightInMeters / 2,
    cLength
  );

  const directionToTravel = homePlacementRotation + angleToCorner;
  const directionToTravel2 = -homePlacementRotation + angleToCorner;

  // here we combine the angle we've calculated to the corner
  // and the distance from the center of the property to the corner
  // to propertly place coordinates and update them.
  const positionOnCornerOne = [
    homePlacementLongitude -
      Math.cos((directionToTravel * Math.PI) / 180) * cLengthDegrees,
    homePlacementLatitude +
      Math.sin((directionToTravel * Math.PI) / 180) * cLengthDegrees,
  ];
  const positionOnCornerTwo = [
    homePlacementLongitude +
      Math.cos((directionToTravel2 * Math.PI) / 180) * cLengthDegrees,
    homePlacementLatitude +
      Math.sin((directionToTravel2 * Math.PI) / 180) * cLengthDegrees,
  ];
  const positionOnCornerThree = [
    homePlacementLongitude +
      Math.cos((directionToTravel * Math.PI) / 180) * cLengthDegrees,
    homePlacementLatitude -
      Math.sin((directionToTravel * Math.PI) / 180) * cLengthDegrees,
  ];
  const positionOnCornerFour = [
    homePlacementLongitude -
      Math.cos((directionToTravel2 * Math.PI) / 180) * cLengthDegrees,
    homePlacementLatitude -
      Math.sin((directionToTravel2 * Math.PI) / 180) * cLengthDegrees,
  ];
  return [
    positionOnCornerOne,
    positionOnCornerTwo,
    positionOnCornerThree,
    positionOnCornerFour,
    positionOnCornerOne, // repeat to close the polygon
  ] as Position[];
};
